import { useCallback, type Dispatch, type SetStateAction } from "react";

export interface UseFormNavigationStateArgs<T> {
  /** The stored half — `undefined` until the enduser has actually navigated. */
  stored: T | undefined;
  setStored: (value: T | undefined) => void;
  /** What to present while the stored half is empty, recomputed from the answers loaded so far. */
  fallbackValue: T;
  /** Defaults to "stored value is undefined"; override where an empty value is a real state. */
  isEmpty?: (stored: T | undefined) => boolean;
}

/**
 * Layers a derived fallback over a navigation value held in the store.
 *
 * Navigation state is genuinely two things at once. Once someone moves through the form, where they
 * are is *their* state and belongs in the store. Before that it is derived: the wizard resolves the
 * opening position from whichever answers have loaded, which is what lets a partly-filled form
 * reopen on the first unanswered question — and that resolution keeps changing as more answers
 * arrive. Storing the derived value would freeze it at whatever had loaded first.
 *
 * So the store holds only the stored half and this hook resolves the rest on read. The returned
 * tuple matches `useState`, including functional updates, which see the resolved value rather than
 * `undefined`.
 */
export const useFormNavigationState = <T>({
  stored,
  setStored,
  fallbackValue,
  isEmpty,
}: UseFormNavigationStateArgs<T>): [T, Dispatch<SetStateAction<T>>] => {
  const resolved = (isEmpty ? isEmpty(stored) : stored === undefined) ? fallbackValue : (stored as T);

  const setState = useCallback(
    (update: SetStateAction<T>) => {
      setStored(update instanceof Function ? update(resolved) : update);
    },
    [setStored, resolved]
  );

  return [resolved, setState];
};
